import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { FileDown, FileSpreadsheet, FileText, Landmark, Receipt, Hash, ChevronRight, FileSpreadsheet as FileReturn, AlertTriangle } from "lucide-react";
import { UnresolvedTdsVendors } from "@/components/UnresolvedTdsVendors";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DateFilters, filterParams } from "@/components/DateFilters";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";
import { toast } from "sonner";

export default function TDSSummary() {
  const [ptype, setPtype] = useState("ap");
  const [filters, setFilters] = useState({ from: null, to: null });
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(() => {
    axios.get(`${API}/reports/tds-summary`, { params: { ptype, ...filterParams(filters) } })
      .then((r) => { setData(r.data); setExpanded(new Set()); }).catch(() => {});
  }, [ptype, filters]);

  useEffect(() => {
    load();
    window.addEventListener("sap-synced", load);
    return () => window.removeEventListener("sap-synced", load);
  }, [load]);

  const toggle = (label) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(label) ? next.delete(label) : next.add(label);
    return next;
  });

  const download = async (format, kind = "summary") => {
    try {
      const path = kind === "return" ? "tds-return" : "tds-summary";
      const res = await axios.get(`${API}/reports/${path}/export`, {
        params: { ptype, ...filterParams(filters), format }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${path}-${ptype}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 200);
    } catch {
      toast.error("Could not generate the export. Please try again.");
    }
  };

  if (!data) return <div className="animate-pulse text-slate-400 text-sm">Loading TDS summary...</div>;
  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="tds-summary-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">TDS Summary</h1>
          <p className="text-sm text-slate-500 mt-1">Withholding tax grouped by section / tax code — for TDS return preparation</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => download("xlsx", "return")} data-testid="tds-return-export" className="border-sky-300 text-sky-700 hover:bg-sky-50">
            <FileReturn className="h-4 w-4 mr-1.5" />{ptype === "ar" ? "27Q" : "26Q"} Return
          </Button>
          <span className="w-px h-6 bg-slate-200" />
          <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="tds-summary-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="tds-summary-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="tds-summary-export-csv"><FileDown className="h-4 w-4 mr-1.5" />CSV</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
          {[["ap", "Payables (AP)"], ["ar", "Receivables (AR)"]].map(([v, label]) => (
            <button key={v} type="button" onClick={() => setPtype(v)}
              className={`px-4 h-9 text-sm font-medium ${ptype === v ? "bg-sky-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid={`tds-ptype-${v}`}>{label}</button>
          ))}
        </div>
        <DateFilters showWbs filters={filters} setFilters={setFilters} testPrefix="tds-summary" fromLabel="From date (optional)" toLabel="As on date" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200 shadow-sm fade-in-up">
          <CardContent className="p-5 flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-2">Total TDS Deducted</p>
              <p className="text-2xl font-black tracking-tighter text-rose-600" data-testid="tds-total">{inrShort(t.tds)}</p>
              <p className="text-xs mt-1.5 text-slate-400">{t.count} deductions</p>
            </div>
            <div className="h-9 w-9 rounded-md bg-rose-50 flex items-center justify-center"><Receipt className="h-4.5 w-4.5 text-rose-600" style={{ height: 18, width: 18 }} /></div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm fade-in-up">
          <CardContent className="p-5 flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-2">Taxable Base</p>
              <p className="text-2xl font-black tracking-tighter text-slate-900" data-testid="tds-base">{inrShort(t.base)}</p>
              <p className="text-xs mt-1.5 text-slate-400">Amount TDS computed on</p>
            </div>
            <div className="h-9 w-9 rounded-md bg-sky-50 flex items-center justify-center"><Landmark className="h-4.5 w-4.5 text-sky-700" style={{ height: 18, width: 18 }} /></div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm fade-in-up">
          <CardContent className="p-5 flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-2">Sections / Codes</p>
              <p className="text-2xl font-black tracking-tighter text-slate-900" data-testid="tds-codes">{t.codes}</p>
              <p className="text-xs mt-1.5 text-slate-400">Distinct withholding tax codes</p>
            </div>
            <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center"><Hash className="h-4.5 w-4.5 text-slate-600" style={{ height: 18, width: 18 }} /></div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-slate-800">By Section / TDS Code</CardTitle>
          <p className="text-xs text-slate-400 mt-0.5">Grouped from SAP withholding tax code (TDS section shown when your tenant populates it) · click a row for the party breakdown</p>
        </CardHeader>
        <CardContent>
          <Table data-testid="tds-summary-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Section / Code</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Taxable Base</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">TDS Deducted</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Deductions</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Parties</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Certificates</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r, i) => {
                const open = expanded.has(r.label);
                return (
                  <Fragment key={i}>
                    <TableRow className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => toggle(r.label)} data-testid={`tds-row-${r.label}`}>
                      <TableCell className="text-sm font-semibold text-slate-800">
                        <span className="inline-flex items-center gap-2">
                          <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
                          {r.label}{r.rate_pct != null ? <span className="text-xs text-slate-400 font-normal"> · {r.rate_pct}%</span> : null}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-600">{inr(r.base)}</TableCell>
                      <TableCell className="text-right text-sm font-semibold text-rose-600">{inr(r.tds)}</TableCell>
                      <TableCell className="text-right text-sm text-slate-600">{r.count}</TableCell>
                      <TableCell className="text-right text-sm text-slate-600">{r.parties}</TableCell>
                      <TableCell className="text-right text-sm text-slate-600">{r.certificates}</TableCell>
                    </TableRow>
                    {open && (r.party_rows || []).map((p, j) => (
                      <TableRow key={`${i}-${j}`} className="bg-slate-50/50 border-b border-slate-100" data-testid={`tds-party-${r.label}-${p.code}`}>
                        <TableCell className="text-sm pl-9">
                          <span className="text-slate-700">{p.name}</span>
                          <span className="text-xs text-slate-400"> · {p.code}{p.pan ? ` · PAN ${p.pan}` : ""}</span>
                        </TableCell>
                        <TableCell className="text-right text-sm text-slate-500">{inr(p.base)}</TableCell>
                        <TableCell className="text-right text-sm text-rose-500">{inr(p.tds)}</TableCell>
                        <TableCell className="text-right text-sm text-slate-500">{p.count}</TableCell>
                        <TableCell className="text-right text-sm text-slate-400">—</TableCell>
                        <TableCell className="text-right text-sm text-slate-500">{p.certificates}</TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                );
              })}
              {data.rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-8">No TDS deductions found. TDS populates from live SAP after a sync.</TableCell></TableRow>
              )}
              {data.rows.length > 0 && (
                <TableRow className="bg-slate-50 font-bold">
                  <TableCell className="text-sm text-slate-800">Total</TableCell>
                  <TableCell className="text-right text-sm text-slate-900">{inr(t.base)}</TableCell>
                  <TableCell className="text-right text-sm text-rose-700">{inr(t.tds)}</TableCell>
                  <TableCell className="text-right text-sm text-slate-900">{t.count}</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {ptype === "ap" && <UnresolvedTdsVendors filters={filterParams(filters)} />}
    </div>
  );
}
